import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  verifyEmail: (token, id) => api.post('/auth/verify-email', { token, id }),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  markWelcomeSeen: () => api.post('/auth/mark-welcome-seen'),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
};

// Edition endpoints
export const editionApi = {
  getAll: () => api.get('/editions'),
  getMine: () => api.get('/editions/my'),
  checkAccess: () => api.get('/editions/check'),
};

// Profile endpoints
export const profileApi = {
  get: () => api.get('/profile'),
  create: (data) => api.post('/profile', data),
  update: (data) => api.put('/profile', data),
};

// User endpoints
export const userApi = {
  updatePhase: (phase) => api.post('/user/update-phase', { phase }),
};

// Session endpoints
export const sessionApi = {
  getActive: () => api.get('/session/active'),
};

// Phase endpoints
export const phaseApi = {
  getAll: () => api.get('/phases'),
  getBySlug: (slug) => api.get(`/phases/${slug}`),
  toggleTick: (slug, itemIndex, ticked) => api.post(`/phases/${slug}/tick`, { item_index: itemIndex, ticked }),
  resetPhase: (slug) => api.post(`/phases/${slug}/reset`),
  resetAll: () => api.post('/phases/reset-all'),
};

// Admin endpoints
export const adminApi = {
  searchUsers: (query, filter = '') => api.get(`/admin/users/search?q=${encodeURIComponent(query)}&filter=${filter}`),
  getUserDetail: (userId) => api.get(`/admin/users/${userId}`),
  forceLogout: (userId) => api.post(`/admin/users/${userId}/force-logout`),
  sendPasswordReset: (userId) => api.post(`/admin/users/${userId}/send-reset`),
  grantEdition: (userId, editionId) => api.post(`/admin/users/${userId}/grant-edition`, { edition_id: editionId }),
  removeEdition: (userId, editionId) => api.post(`/admin/users/${userId}/remove-edition`, { edition_id: editionId }),
  suspendUser: (userId) => api.post(`/admin/users/${userId}/suspend`),
  unsuspendUser: (userId) => api.post(`/admin/users/${userId}/unsuspend`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  resendVerification: (userId) => api.post(`/admin/users/${userId}/resend-verification`),
  bulkDeleteUsers: (userIds, confirmationText) => api.post('/admin/users/bulk-delete', { user_ids: userIds, confirmation_text: confirmationText }),
};

// Product endpoints
export const productApi = {
  getAll: () => api.get('/products'),
  get: (productId) => api.get(`/products/${productId}`),
  getUserProducts: () => api.get('/user/products'),
};

// Stripe/Payment endpoints
export const stripeApi = {
  getPrice: () => api.get('/stripe/price'),
  createCheckoutSession: (productId, originUrl) => api.post('/stripe/create-checkout-session', { 
    product_id: productId, 
    origin_url: originUrl
    // NO price_id sent - backend fetches it directly from Stripe
  }),
  getCheckoutStatus: (sessionId) => api.get(`/stripe/checkout-status/${sessionId}`),
};

// Fault Help / Instructor Panel endpoints
export const faultHelpApi = {
  getAll: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.category) searchParams.append('category', params.category);
    if (params.search) searchParams.append('search', params.search);
    if (params.limit) searchParams.append('limit', params.limit);
    const query = searchParams.toString();
    return api.get(`/fault-help${query ? '?' + query : ''}`);
  },
  getBySlug: (slug) => api.get(`/fault-help/${slug}`),
  getCategories: () => api.get('/fault-help/categories/list'),
};

// In-Flight Assistance System endpoints
export const assistanceApi = {
  // Fault search with alias-based priority matching (min 2 chars, max 5 results)
  searchFaults: (query) => api.get(`/assistance/search?q=${encodeURIComponent(query)}`),
  // Get all faults, optionally filtered by system category
  getFaults: (system = null) => {
    const params = system && system !== 'ALL' ? `?system=${encodeURIComponent(system)}` : '';
    return api.get(`/assistance/faults${params}`);
  },
  // Get all fault categories with counts
  getCategories: () => api.get('/assistance/categories'),
  // Get all 13 abnormal/emergency procedure quick-reference cards
  getAbnormalProcedures: () => api.get('/assistance/abnormal-procedures'),
  // Get all 121 MEL (Minimum Equipment List) items
  getMelItems: () => api.get('/assistance/mel-items'),
};

// Explore the Cockpit endpoints
export const cockpitApi = {
  getExplorer: () => api.get('/cockpit/explorer'),
};

// Paid-only checklist PDF downloads (watermarked on the fly server-side)
export const downloadApi = {
  getFile: (fileKey) => api.get(`/downloads/${fileKey}`, { responseType: 'blob' }),
  // MCDU Guide + Quick Reference Cards downloads (added 2026-10-15)
  getList: () => api.get('/downloads'),
};

export default api;
